import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { AppointmentService } from 'services/appointments';
import { type Appointment } from 'types/appointments';

interface VerifyReportNotificationVariables {
  appointmentId: string;
  patientId: string;
}

export const useVerifyReportNotification = (): UseMutationResult<
  Appointment,
  Error,
  VerifyReportNotificationVariables
> => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  return useMutation({
    mutationKey: [MUTATION_KEYS.VERIFY_REPORT_NOTIFICATION],
    mutationFn: async ({ appointmentId }) => {
      const { data } = await AppointmentService.verifyReportNotification(appointmentId);
      return data;
    },
    onSuccess: async appointment => {
      if (!appointment.reportVerifiedAt) return;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_REPORT_NOTIFICATIONS] }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.FETCH_REPORT_NOTIFICATIONS_COUNT],
        }),
      ]);
    },
    onError: () => {
      toast.error(t('NotificationsPage.verify.error'));
    },
    onSettled: (_data, _error, { patientId }) => {
      void navigate(generatePath(ROUTES.PATIENT_PROFILE, { id: patientId }));
    },
  });
};
